import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Alert, CssBaseline, ThemeProvider } from '@mui/material';
import './i18n/i18n'; // Initialize i18n

// Components
import ModernLayout from './components/ModernLayout';
import ModernDashboard from './pages/ModernDashboard';
import Sandbox from './pages/Sandbox';
import Monitoring from './pages/Monitoring';
import Benchmarks from './pages/Benchmarks';
import Security from './pages/Security';
import APIPlayground from './pages/APIPlayground';
import ApiDocumentation from './pages/ApiDocumentation';

// Services and Context
import { apiService } from './services/api';
import { websocketService } from './services/websocket';
import { AppProvider } from './context/AppContext';
import { createAppTheme } from './theme/theme';
import i18n from './i18n/i18n';
function App() {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState('dashboard');
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load theme from localStorage or default to 'light'
    return localStorage.getItem('theme') || 'light';
  });
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Load language from localStorage or default to 'de' (German)
    return localStorage.getItem('language') || 'de';
  });

  const theme = React.useMemo(() => {
    try {
      const validTheme = currentTheme || 'light';
      return createAppTheme(validTheme);
    } catch (error) {
      // Fallback to default light theme
      return createAppTheme('light');
    }
  }, [currentTheme]);

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    // Save theme to localStorage whenever it changes
    localStorage.setItem('theme', currentTheme);
    // Set theme attribute on document element for chart styling
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    // Save language to localStorage and change i18n language
    localStorage.setItem('language', currentLanguage);
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage, i18n]);

  const initializeApp = async () => {
    try {
      // Check backend connectivity
      const health = await apiService.getHealth();
      setSystemStatus(health);

      // Initialize WebSocket connection
      websocketService.connect();

      setLoading(false);
    } catch (err) {
      setError(t('failed_to_connect_to_backend'));
      setLoading(false);
    }
  };

  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
  };

  const toggleTheme = () => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setCurrentTheme(newTheme);
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'de' ? 'en' : 'de';
    setCurrentLanguage(newLanguage);
  };

  const renderCurrentPage = () => {
    switch (currentTab) {
      case 'dashboard':
        return <ModernDashboard onNavigate={handleTabChange} />;
      case 'sandbox':
        return <Sandbox />;
      case 'monitoring':
        return <Monitoring />;
      case 'benchmarks':
        return <Benchmarks />;
      case 'security':
        return <Security onNavigate={handleTabChange} />;
      case 'playground':
        return <APIPlayground />;
      case 'api':
        return <ApiDocumentation />;
      default:
        return <ModernDashboard onNavigate={handleTabChange} />;
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          p={3}
        >
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppProvider>
        <ModernLayout 
          currentTab={currentTab}
          onTabChange={handleTabChange}
          systemStatus={systemStatus}
          onThemeToggle={toggleTheme}
          currentTheme={currentTheme}
          onLanguageToggle={toggleLanguage}
          currentLanguage={currentLanguage}
        >
          {renderCurrentPage()}
        </ModernLayout>
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
